import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DonationQuickAction from '../../components/ui/DonationQuickAction';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ChallengeCard from './components/ChallengeCard';
import LeaderboardCard from './components/LeaderboardCard';
import ChallengeFilters from './components/ChallengeFilters';
import FeaturedChallenge from './components/FeaturedChallenge';
import ParticipationModal from './components/ParticipationModal';
import ChallengeStats from './components/ChallengeStats';

const GivingChallenges = () => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [filteredChallenges, setFilteredChallenges] = useState([]);
  const [featuredChallenge, setFeaturedChallenge] = useState(null);
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    sort: 'newest',
    search: ''
  });
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [isParticipationModalOpen, setIsParticipationModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Mock data
  const mockChallenges = [
    {
      id: 1,
      title: "Clean Water for Rural Communities",
      description: "Help us build water wells and filtration systems in underserved rural areas. Every dollar brings clean water closer to families in need.",
      category: "poverty",
      image: "https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=800&h=600&fit=crop",
      goalAmount: 50000,
      currentAmount: 32500,
      participantCount: 245,
      status: "active",
      endDate: "2025-01-15T23:59:59",
      isParticipating: false,
      badges: ["Trending", "High Impact"],
      topContributors: [
        { id: 1, name: "Sarah Johnson", amount: 500 },
        { id: 2, name: "Michael Chen", amount: 350 },
        { id: 3, name: "Emma Davis", amount: 300 }
      ]
    },
    {
      id: 2,
      title: "Education Emergency Fund",
      description: "Support students affected by natural disasters with emergency educational supplies, temporary learning spaces, and technology access.",
      category: "education",
      image: "https://images.pexels.com/photos/8613089/pexels-photo-8613089.jpeg?w=800&h=600&fit=crop",
      goalAmount: 75000,
      currentAmount: 68200,
      participantCount: 412,
      status: "ending-soon",
      endDate: "2025-01-12T23:59:59",
      isParticipating: true,
      badges: ["Ending Soon", "90% Complete"],
      topContributors: [
        { id: 4, name: "David Wilson", amount: 1000 },
        { id: 5, name: "Lisa Rodriguez", amount: 750 },
        { id: 6, name: "James Thompson", amount: 600 }
      ]
    },
    {
      id: 3,
      title: "Wildlife Conservation Challenge",
      description: "Protect endangered species through habitat preservation, anti-poaching efforts, and community education programs in critical ecosystems.",
      category: "environment",
      image: "https://images.pixabay.com/photo/2018/05/03/22/34/lion-3372720_1280.jpg?w=800&h=600&fit=crop",
      goalAmount: 100000,
      currentAmount: 45800,
      participantCount: 189,
      status: "active",
      endDate: "2025-02-28T23:59:59",
      isParticipating: false,
      badges: ["New", "Wildlife"],
      topContributors: [
        { id: 7, name: "Anna Martinez", amount: 800 },
        { id: 8, name: "Robert Kim", amount: 650 },
        { id: 9, name: "Jennifer Lee", amount: 500 }
      ]
    },
    {
      id: 4,
      title: "Medical Equipment for Hospitals",
      description: "Provide life-saving medical equipment to hospitals in developing regions, including ventilators, monitors, and surgical instruments.",
      category: "healthcare",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=600&fit=crop",
      goalAmount: 120000,
      currentAmount: 89400,
      participantCount: 356,
      status: "active",
      endDate: "2025-01-30T23:59:59",
      isParticipating: true,
      badges: ["High Impact", "Medical"],
      topContributors: [
        { id: 10, name: "Dr. Patricia Brown", amount: 1200 },
        { id: 11, name: "Mark Anderson", amount: 900 },
        { id: 12, name: "Susan Taylor", amount: 750 }
      ]
    },
    {
      id: 5,
      title: "Disaster Relief Response",
      description: "Rapid response fund for natural disaster victims, providing emergency shelter, food, medical care, and rebuilding support.",
      category: "disaster",
      image: "https://images.pexels.com/photos/6995245/pexels-photo-6995245.jpeg?w=800&h=600&fit=crop",
      goalAmount: 200000,
      currentAmount: 156700,
      participantCount: 623,
      status: "active",
      endDate: "2025-03-15T23:59:59",
      isParticipating: false,
      badges: ["Urgent", "Emergency"],
      topContributors: [
        { id: 13, name: "Community Foundation", amount: 5000 },
        { id: 14, name: "Global Relief Corp", amount: 3500 },
        { id: 15, name: "Local Business Alliance", amount: 2800 }
      ]
    },
    {
      id: 6,
      title: "Animal Shelter Support",
      description: "Help local animal shelters provide food, medical care, and loving homes for abandoned and rescued animals in our community.",
      category: "animals",
      image: "https://images.pixabay.com/photo/2017/09/25/13/12/dog-2785074_1280.jpg?w=800&h=600&fit=crop",
      goalAmount: 30000,
      currentAmount: 28900,
      participantCount: 178,
      status: "ending-soon",
      endDate: "2025-01-10T23:59:59",
      isParticipating: true,
      badges: ["Almost Complete", "Animals"],
      topContributors: [
        { id: 16, name: "Animal Lovers United", amount: 1500 },
        { id: 17, name: "Pet Care Clinic", amount: 1200 },
        { id: 18, name: "Sarah Williams", amount: 800 }
      ]
    }
  ];

  const mockGlobalLeaderboard = [
    {
      id: 1,
      name: "Community Foundation",
      totalAmount: 15000,
      donationCount: 25,
      badges: ["Top Donor", "Consistent Giver"]
    },
    {
      id: 2,
      name: "Dr. Patricia Brown",
      totalAmount: 8500,
      donationCount: 18,
      badges: ["Medical Hero"]
    },
    {
      id: 3,
      name: "Global Relief Corp",
      totalAmount: 7200,
      donationCount: 12,
      badges: ["Corporate Partner"]
    },
    {
      id: 4,
      name: "Sarah Johnson",
      totalAmount: 6800,
      donationCount: 22,
      badges: ["Frequent Donor"]
    },
    {
      id: 5,
      name: "Michael Chen",
      totalAmount: 5400,
      donationCount: 15,
      badges: ["Rising Star"]
    },
    {
      id: 6,
      name: "Emma Davis",
      totalAmount: 4900,
      donationCount: 19,
      badges: ["Dedicated Supporter"]
    },
    {
      id: 7,
      name: "David Wilson",
      totalAmount: 4200,
      donationCount: 11,
      badges: ["Education Champion"]
    },
    {
      id: 8,
      name: "Lisa Rodriguez",
      totalAmount: 3800,
      donationCount: 14,
      badges: ["Community Builder"]
    }
  ];

  const mockStats = {
    totalRaised: 1250000,
    raisedGrowth: 23,
    activeParticipants: 2847,
    participantGrowth: 156,
    completedChallenges: 42,
    completionRate: 89,
    successRate: 94,
    successGrowth: 8
  };

  useEffect(() => {
    // Simulate API call
    const loadData = async () => {
      setIsLoading(true);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setChallenges(mockChallenges);
      setFeaturedChallenge(mockChallenges?.[0]);
      setGlobalLeaderboard(mockGlobalLeaderboard);
      setStats(mockStats);
      setIsLoading(false);
    };

    loadData();
  }, []);

  useEffect(() => {
    let filtered = [...challenges];

    // Apply category filter
    if (filters?.category !== 'all') {
      filtered = filtered?.filter(challenge => challenge?.category === filters?.category);
    }

    // Apply status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(challenge => challenge?.status === filters?.status);
    }

    // Apply search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter(challenge =>
        challenge?.title?.toLowerCase()?.includes(searchTerm) ||
        challenge?.description?.toLowerCase()?.includes(searchTerm) ||
        challenge?.category?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Apply sorting
    switch (filters?.sort) {
      case 'ending-soon':
        filtered?.sort((a, b) => new Date(a.endDate) - new Date(b.endDate));
        break;
      case 'most-popular':
        filtered?.sort((a, b) => b?.participantCount - a?.participantCount);
        break;
      case 'highest-goal':
        filtered?.sort((a, b) => b?.goalAmount - a?.goalAmount);
        break;
      case 'most-raised':
        filtered?.sort((a, b) => b?.currentAmount - a?.currentAmount);
        break;
      default: // newest
        filtered?.sort((a, b) => b?.id - a?.id);
    }

    setFilteredChallenges(filtered);
  }, [challenges, filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      category: 'all',
      status: 'all',
      sort: 'newest',
      search: ''
    });
  };

  const handleParticipate = (challenge) => {
    setSelectedChallenge(challenge);
    setIsParticipationModalOpen(true);
  };

  const handleConfirmParticipation = (participationData) => {
    console.log('Participation confirmed:', participationData);
    
    // Update challenge participation status
    setChallenges(prev => prev?.map(challenge => 
      challenge?.id === participationData?.challengeId
        ? { ...challenge, isParticipating: true, currentAmount: challenge?.currentAmount + participationData?.amount }
        : challenge
    ));

    setIsParticipationModalOpen(false);
    setSelectedChallenge(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)]?.map((_, i) => (
                <div key={i} className="h-32 bg-muted rounded-lg"></div>
              ))}
            </div>
            <div className="h-64 bg-muted rounded-lg"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[...Array(6)]?.map((_, i) => (
                <div key={i} className="h-96 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Giving Challenges
            </h1>
            <p className="text-muted-foreground">
              Join time-limited campaigns and compete with the community to make a bigger impact
            </p>
          </div>
          <Button
            onClick={() => navigate('/cause-discovery')}
            variant="outline"
            iconName="Search"
            iconPosition="left"
          >
            Discover Causes
          </Button>
        </div>

        {/* Challenge Stats */}
        <ChallengeStats stats={stats} />

        {/* Featured Challenge */}
        {featuredChallenge && (
          <FeaturedChallenge
            challenge={featuredChallenge}
            onParticipate={handleParticipate}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Filters */}
            <ChallengeFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />

            {/* Challenge Grid */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">
                  {filters?.category === 'all' ? 'All Challenges' : `${filters?.category?.charAt(0)?.toUpperCase() + filters?.category?.slice(1)} Challenges`}
                </h2>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Filter" size={16} />
                  <span>{filteredChallenges?.length} challenges found</span>
                </div>
              </div>

              {filteredChallenges?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredChallenges?.map((challenge) => (
                    <ChallengeCard
                      key={challenge?.id}
                      challenge={challenge}
                      onParticipate={handleParticipate}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    No challenges found
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your filters or search terms
                  </p>
                  <Button
                    onClick={handleClearFilters}
                    variant="outline"
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Global Leaderboard */}
            <LeaderboardCard leaderboard={globalLeaderboard} />

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-card-foreground mb-4">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/cause-discovery')}
                  variant="outline"
                  className="w-full justify-start"
                  iconName="Search"
                  iconPosition="left"
                >
                  Discover New Causes
                </Button>
                <Button
                  onClick={() => navigate('/user-dashboard')}
                  variant="outline"
                  className="w-full justify-start"
                  iconName="BarChart3"
                  iconPosition="left"
                >
                  View My Impact
                </Button>
                <Button
                  onClick={() => navigate('/community-feed')}
                  variant="outline"
                  className="w-full justify-start"
                  iconName="Users"
                  iconPosition="left"
                >
                  Join Community
                </Button>
              </div>
            </div>

            {/* Challenge Tips */}
            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-border rounded-lg p-6">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Lightbulb" size={20} className="text-primary" />
                <h3 className="text-lg font-semibold text-card-foreground">
                  Challenge Tips
                </h3>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Join challenges early for maximum impact</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Share challenges to increase participation</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Set up recurring donations for consistency</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Track your progress on the leaderboard</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      {/* Participation Modal */}
      {selectedChallenge && (
        <ParticipationModal
          challenge={selectedChallenge}
          isOpen={isParticipationModalOpen}
          onClose={() => {
            setIsParticipationModalOpen(false);
            setSelectedChallenge(null);
          }}
          onConfirm={handleConfirmParticipation}
        />
      )}
      {/* Donation Quick Action */}
      <DonationQuickAction />
    </div>
  );
};

export default GivingChallenges;